/**
 * Page de validation des inscriptions via partenaires sociaux
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { inscriptionService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { notify } from '../components/NotificationContainer';
import { prompt } from '../components/PromptContainer';
import { confirm } from '../components/ConfirmContainer';
import logger from '../utils/logger';

const ValidationPartenaires = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inscriptions, setInscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('EN_ATTENTE_VALIDATION_PARTENAIRE');

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      navigate('/');
      return;
    }
    loadInscriptions();
  }, [user, navigate, filter, currentPage]);

  // Réinitialiser la page à 1 quand le filtre change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  // Recharger les données quand on revient sur la page
  useEffect(() => {
    const handleFocus = () => {
      if (document.visibilityState === 'visible') {
        loadInscriptions();
      }
    };

    document.addEventListener('visibilitychange', handleFocus);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleFocus);
      window.removeEventListener('focus', handleFocus);
    };
  }, [filter]);

  const loadInscriptions = async () => {
    try {
      setLoading(true);

      // OPTIMISATION: Utiliser le filtrage backend au lieu du filtrage client
      // Mapper les filtres frontend vers les paramètres backend
      const filterMapping = {
        'EN_ATTENTE_VALIDATION_PARTENAIRE': 'EN_ATTENTE',
        'VALIDEES': 'VALIDEES',
        'REFUSEES': 'REFUSEES',
        'TOUTES': 'TOUTES'
      };

      const statutPartenaire = filterMapping[filter] || 'EN_ATTENTE';

      // Appeler l'API avec le filtre backend et la pagination
      const response = await inscriptionService.getAll({
        statut_partenaire: statutPartenaire,
        page: currentPage
      });

      // Gérer la réponse paginée ou non paginée
      if (response.data.results) {
        // Réponse paginée (VALIDEES, REFUSEES, TOUTES)
        setInscriptions(response.data.results);
        setTotalCount(response.data.count);

        // Calculer le nombre de pages (PAGE_SIZE = 20 dans le backend)
        const pageSize = 20;
        setTotalPages(Math.ceil(response.data.count / pageSize));
      } else {
        // Réponse non paginée (EN_ATTENTE)
        const allInscriptions = Array.isArray(response.data) ? response.data : [];
        setInscriptions(allInscriptions);
        setTotalCount(allInscriptions.length);
        setTotalPages(1);  // Une seule page car pas de pagination
      }

      logger.log(`[ValidationPartenaires] Chargé ${response.data.results?.length || response.data.length} inscriptions (filtre: ${statutPartenaire}, page: ${currentPage})`);
    } catch (error) {
      logger.error('Erreur lors du chargement:', error);
      notify.error('Erreur lors du chargement des inscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleValider = async (inscriptionId) => {
    if (!await confirm.ask('Êtes-vous sûr de vouloir valider cette inscription partenaire ?', 'Valider l\'inscription')) return;

    try {
      await inscriptionService.validerPartenaire(inscriptionId);
      notify.success('Inscription validée avec succès');

      // OPTIMISATION: Recharger les données depuis le backend
      // Plus besoin de gérer le state local complexe
      loadInscriptions();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la validation');
    }
  };

  const handleRefuser = async (inscriptionId) => {
    const motif = await prompt.askRefusalReason();
    if (!motif) return;

    try {
      await inscriptionService.refuserPartenaire(inscriptionId, motif);
      notify.success('Inscription refusée');

      // OPTIMISATION: Recharger les données depuis le backend
      loadInscriptions();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors du refus');
    }
  };

  const getStatutBadge = (inscription) => {
    if (inscription.partenaire_valide) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="h-4 w-4 mr-1" />
          Validé
        </span>
      );
    }
    if (inscription.statut === 'ANNULEE') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircleIcon className="h-4 w-4 mr-1" />
          Refusé
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <ClockIcon className="h-4 w-4 mr-1" />
        En attente
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen">
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Validation des inscriptions partenaires</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">
            Gérez les inscriptions effectuées via les partenaires sociaux
          </p>
        </div>

        {/* Filtres */}
        <div className="mb-4 sm:mb-6 bg-white shadow-sm border border-gray-200 rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <button
              onClick={() => setFilter('EN_ATTENTE_VALIDATION_PARTENAIRE')}
              className={`px-3 sm:px-4 py-2 rounded-md font-medium transition-colors text-sm sm:text-base ${
                filter === 'EN_ATTENTE_VALIDATION_PARTENAIRE'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-400'
              }`}
            >
              En attente
            </button>
            <button
              onClick={() => setFilter('VALIDEES')}
              className={`px-3 sm:px-4 py-2 rounded-md font-medium transition-colors text-sm sm:text-base ${
                filter === 'VALIDEES'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-400'
              }`}
            >
              Validées
            </button>
            <button
              onClick={() => setFilter('REFUSEES')}
              className={`px-3 sm:px-4 py-2 rounded-md font-medium transition-colors text-sm sm:text-base ${
                filter === 'REFUSEES'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-400'
              }`}
            >
              Refusées
            </button>
            <button
              onClick={() => setFilter('TOUTES')}
              className={`px-3 sm:px-4 py-2 rounded-md font-medium transition-colors text-sm sm:text-base ${
                filter === 'TOUTES'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-400'
              }`}
            >
              Toutes
            </button>
          </div>
        </div>

        {/* Compteur de résultats */}
        <div className="mb-4 text-sm text-gray-600">
          {totalCount > 0 ? (
            <span>
              Affichage de {inscriptions.length} résultat{inscriptions.length > 1 ? 's' : ''} sur {totalCount} au total
              {totalPages > 1 && ` (page ${currentPage} sur ${totalPages})`}
            </span>
          ) : (
            <span>Aucun résultat</span>
          )}
        </div>

        {/* Liste des inscriptions */}
        {inscriptions.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 text-center">
            <p className="text-sm sm:text-base text-gray-500">Aucune inscription à valider</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:gap-6">
          {inscriptions.map((inscription) => (
            <div key={inscription.id} className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
              <div className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  {/* Infos principales */}
                  <div className="flex-1">
                    <div className="flex items-start gap-3 sm:gap-4 mb-4">
                      <div className="bg-primary-100 rounded-full p-2 sm:p-3 flex-shrink-0">
                        <svg className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                          {inscription.enfant_details?.prenom} {inscription.enfant_details?.nom}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500">{inscription.enfant_details?.age} ans</p>
                      </div>
                      <div className="flex-shrink-0">
                        {getStatutBadge(inscription)}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                      <div className="flex items-start">
                        <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-gray-900">{inscription.sejour_details?.titre}</p>
                          <p className="text-xs text-gray-500">
                            Du {new Date(inscription.sejour_details?.date_debut).toLocaleDateString('fr-FR')} au{' '}
                            {new Date(inscription.sejour_details?.date_fin).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-gray-900">Partenaire</p>
                          <p className="text-xs text-gray-500">
                            {inscription.partenaire_details?.nom || inscription.partenaire_affilie || 'Non renseigné'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-gray-900">Date d'inscription</p>
                          <p className="text-xs text-gray-500">{new Date(inscription.date_inscription).toLocaleDateString('fr-FR')}</p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-gray-900">Montant</p>
                          <p className="text-xs text-gray-500">{inscription.montant_total} €</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                {inscription.statut === 'EN_ATTENTE_VALIDATION_PARTENAIRE' && !inscription.partenaire_valide && (
                  <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                    <button
                      onClick={() => navigate(`/admin/inscriptions/${inscription.id}`)}
                      className="w-full sm:w-auto px-3 sm:px-4 py-2 border border-gray-300 rounded-md text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors"
                    >
                      Voir les détails
                    </button>
                    <button
                      onClick={() => handleRefuser(inscription.id)}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2 border border-transparent rounded-md shadow-sm text-xs sm:text-sm font-medium text-white bg-red-600 hover:bg-red-700 active:bg-red-800 transition-colors flex items-center justify-center"
                    >
                      <XCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                      Refuser
                    </button>
                    <button
                      onClick={() => handleValider(inscription.id)}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2 border border-transparent rounded-md shadow-sm text-xs sm:text-sm font-medium text-white bg-green-600 hover:bg-green-700 active:bg-green-800 transition-colors flex items-center justify-center"
                    >
                      <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                      Valider l'inscription
                    </button>
                  </div>
                )}

                {(inscription.partenaire_valide || inscription.statut === 'ANNULEE') && (
                  <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200 flex justify-end">
                    <button
                      onClick={() => navigate(`/admin/inscriptions/${inscription.id}`)}
                      className="w-full sm:w-auto px-3 sm:px-4 py-2 border border-gray-300 rounded-md text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors"
                    >
                      Voir les détails
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

        {/* Pagination - Toujours affichée */}
        <div className="mt-6 bg-white shadow-sm border border-gray-200 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Info pagination */}
            <div className="text-sm text-gray-700">
              {totalCount === 0 ? (
                <span>Aucun résultat</span>
              ) : totalPages === 1 ? (
                <span>
                  {totalCount} résultat{totalCount > 1 ? 's' : ''} • Page 1/1
                </span>
              ) : (
                <span>
                  Page {currentPage} sur {totalPages} • {totalCount} résultat{totalCount > 1 ? 's' : ''} au total
                </span>
              )}
            </div>

            {/* Boutons de navigation - toujours visibles */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1 || loading}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  currentPage === 1 || loading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                }`}
                title="Première page"
              >
                ⏮
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || loading}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  currentPage === 1 || loading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                }`}
                title="Page précédente"
              >
                ←
              </button>

              {/* Indicateur de page actuelle */}
              <div className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium min-w-[80px] text-center">
                {currentPage} / {totalPages}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || loading}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  currentPage === totalPages || loading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                }`}
                title="Page suivante"
              >
                →
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || loading}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  currentPage === totalPages || loading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                }`}
                title="Dernière page"
              >
                ⏭
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidationPartenaires;
